import React, { useEffect, useState } from "react";
import { Modal, Button, Input } from "./common";
import { planilhaService } from "../services/planilhaService";
import { Planilha, UpdatePlanilhaDTO } from "../types";
import { HiSave, HiX } from "react-icons/hi";
import {
  WRIKE_PIT_ERRO_FORMATO,
  WRIKE_PIT_PLACEHOLDER,
  linkPitValido,
} from "../utils/wrikeLink";

export const UF_ESTADOS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

interface EditarPlanilhaModalProps {
  planilha: Planilha | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}

interface FormState {
  evento: string;
  dataInicio: string;
  dataFim: string;
  localEvento: string;
  ufEvento: string;
  linkProjeto: string;
}

const emptyForm: FormState = {
  evento: "",
  dataInicio: "",
  dataFim: "",
  localEvento: "",
  ufEvento: "",
  linkProjeto: "",
};

export const EditarPlanilhaModal: React.FC<EditarPlanilhaModalProps> = ({
  planilha,
  isOpen,
  onClose,
  onSaved,
  onError,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preenche o formulário com os dados atuais sempre que uma planilha é aberta
  useEffect(() => {
    if (planilha) {
      setForm({
        evento: planilha.evento ?? "",
        dataInicio: planilha.dataInicio ?? "",
        dataFim: planilha.dataFim ?? "",
        localEvento: planilha.localEvento ?? "",
        ufEvento: planilha.ufEvento ?? "",
        linkProjeto: planilha.linkProjeto ?? "",
      });
      setError(null);
    }
  }, [planilha]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const linkAnterior = planilha?.linkProjeto?.trim() ?? "";
  const linkNovo = form.linkProjeto.trim();
  const linkComFormatoInvalido = !!linkNovo && !linkPitValido(linkNovo);
  const linkMudou = linkNovo !== linkAnterior;
  // Trocar um link por outro solta a planilha da OC atual (ver API,
  // AplicarLinkProjetoAsync). Primeiro link não solta nada.
  const trocaDeProjeto = !!linkAnterior && linkMudou;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!planilha) return;

    if (!form.evento.trim()) {
      setError("O campo Evento/Ação é obrigatório.");
      return;
    }
    if (!form.dataInicio) {
      setError("A Data de Início é obrigatória.");
      return;
    }
    if (form.dataFim && form.dataFim < form.dataInicio) {
      setError("A Data de Fim não pode ser anterior à Data de Início.");
      return;
    }

    if (linkAnterior && !linkNovo) {
      setError("O Link PIT não pode ficar em branco depois de preenchido.");
      return;
    }
    if (linkComFormatoInvalido) {
      setError(WRIKE_PIT_ERRO_FORMATO);
      return;
    }

    const payload: UpdatePlanilhaDTO = {
      evento: form.evento.trim(),
      dataInicio: form.dataInicio,
      dataFim: form.dataFim || null,
      localEvento: form.localEvento.trim() || null,
      ufEvento: form.ufEvento || null,
      // Só vai quando muda: a API consulta o Wrike a cada link recebido.
      ...(linkMudou ? { linkProjeto: linkNovo } : {}),
    };

    try {
      setSubmitting(true);
      setError(null);
      await planilhaService.update(planilha.id, payload);
      onSaved();
      onClose();
    } catch (err: any) {
      console.error(err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Não foi possível salvar as alterações.";
      setError(msg);
      onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Planilha"
      size="medium"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            <HiX className="h-4 w-4" />
            Cancelar
          </Button>
          <Button
            type="submit"
            form="editar-planilha-form"
            variant="success"
            loading={submitting}
            disabled={submitting}
          >
            <HiSave className="h-4 w-4" />
            Salvar
          </Button>
        </>
      }
    >
      <form
        id="editar-planilha-form"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        {error && (
          <div className="alert alert-error text-sm py-2">
            <span>{error}</span>
          </div>
        )}

        {/* Cliente — somente leitura */}
        <Input
          label="Cliente"
          type="text"
          value={planilha?.clienteNome ?? ""}
          disabled
          fullWidth
          helperText="O cliente não pode ser alterado."
        />

        <Input
          label="Evento/Ação *"
          type="text"
          name="evento"
          value={form.evento}
          onChange={handleChange}
          required
          placeholder="Ex: ASSINATURA PROJETOS SOCIAIS"
          fullWidth
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Data de Início *"
            type="date"
            name="dataInicio"
            value={form.dataInicio}
            onChange={handleChange}
            required
            fullWidth
          />
          <Input
            label="Data de Fim"
            type="date"
            name="dataFim"
            value={form.dataFim}
            onChange={handleChange}
            fullWidth
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Cidade do Evento"
            type="text"
            name="localEvento"
            value={form.localEvento}
            onChange={handleChange}
            placeholder="Ex: São Paulo"
            fullWidth
          />

          <div className="form-control w-full">
            <label className="label">
              <span className="label-text font-medium text-base-content">
                UF do Evento
              </span>
            </label>
            <select
              name="ufEvento"
              value={form.ufEvento}
              onChange={handleChange}
              className="select select-bordered w-full"
            >
              <option value="">Selecione a UF</option>
              {UF_ESTADOS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Input
          label="Link PIT"
          type="url"
          name="linkProjeto"
          value={form.linkProjeto}
          onChange={handleChange}
          placeholder={WRIKE_PIT_PLACEHOLDER}
          fullWidth
          error={linkComFormatoInvalido ? WRIKE_PIT_ERRO_FORMATO : undefined}
          helperText={
            linkComFormatoInvalido || trocaDeProjeto
              ? undefined
              : "O PIT é extraído deste link."
          }
        />

        {trocaDeProjeto && !linkComFormatoInvalido && (
          <div className="alert alert-warning text-sm py-2">
            <span>
              {planilha?.numeroOC ? (
                <>
                  A planilha deixa a <strong>OC {planilha.numeroOC}</strong>.
                  O próximo envio ao Wrike cria uma OC nova, com outro número,
                  na pasta do novo projeto. A OC atual continua no Wrike como
                  está.
                </>
              ) : (
                <>
                  O PIT passa a ser o do novo projeto, e o próximo envio ao
                  Wrike vai para a pasta dele.
                </>
              )}
            </span>
          </div>
        )}
      </form>
    </Modal>
  );
};
